import { useState, useEffect } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { Search, Building2 } from 'lucide-react'
import { RiskAssessmentDashboard } from '@/components/lender/RiskAssessmentDashboard'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useToast } from '@/components/ui/MobileToast'
import { businessService, BusinessListItem } from '@/services/businessService'
import { Business, TLBHScore, RiskAssessment } from '@/types'

export function RiskAssessmentPage() {
  const { businessId } = useParams<{ businessId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [business, setBusiness] = useState<any>(null)
  const [score, setScore] = useState<TLBHScore | null>(null)
  const [riskAssessment, setRiskAssessment] = useState<RiskAssessment | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [businessSearchQuery, setBusinessSearchQuery] = useState('')
  const [businessSearchResults, setBusinessSearchResults] = useState<BusinessListItem[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const showToast = useToast()

  useEffect(() => {
    const targetBusinessId = businessId || searchParams.get('businessId')
    if (targetBusinessId) {
      loadRiskAssessment(targetBusinessId)
    }
  }, [businessId, searchParams])

  const handleBusinessSearch = async () => {
    if (!businessSearchQuery.trim()) return
    setIsSearching(true)
    try {
      const response = await businessService.getBusinesses({
        search: businessSearchQuery.trim(),
        limit: 10
      })
      setBusinessSearchResults(response.businesses)
    } catch (error) {
      console.error('Business search failed:', error)
      showToast.showToast({
        type: 'error',
        title: 'Search Failed',
        message: 'Unable to search businesses.'
      })
    } finally {
      setIsSearching(false)
    }
  }

  const handleSelectBusiness = (selectedBusiness: BusinessListItem) => {
    setBusinessSearchQuery('')
    setBusinessSearchResults([])
    navigate(`/lender/risk-assessment?businessId=${selectedBusiness.id}`)
  }

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleBusinessSearch()
    }
  }

  const loadRiskAssessment = async (targetBusinessId: string) => {
      setIsLoading(true)
      try {
        let businessData: any
        
        // Fetch specific business
        businessData = await businessService.getBusinessById(targetBusinessId)
        
        setBusiness(businessData)
        
        // Get red flags
        let redFlags: any[] = []
        try {
          const redFlagsResponse = await businessService.getRedFlags(businessData.id)
          redFlags = redFlagsResponse.redFlags || []
        } catch (error) {
          console.error('Failed to load red flags:', error)
        }
        
        // Convert business data to TLBHScore format
        const tlbhScore: TLBHScore = {
          id: businessData.id,
          businessId: businessData.id,
          tlbhScore: parseFloat(businessData.score || '0'),
          band: businessData.band,
          confidence: parseFloat(businessData.confidence || '0'),
          dimensionScores: {
            legal: {
              score: parseFloat(businessData.legal_score || '0'),
              weight: 0.25,
              explanation: 'Legal compliance and regulatory adherence',
              verificationPoints: [],
              trend: 'stable',
              lastUpdated: businessData.calculated_at || businessData.created_at
            },
            financial: {
              score: parseFloat(businessData.financial_score || '0'),
              weight: 0.30,
              explanation: 'Financial health and stability',
              verificationPoints: [],
              trend: 'stable',
              lastUpdated: businessData.calculated_at || businessData.created_at
            },
            operational: {
              score: parseFloat(businessData.operational_score || '0'),
              weight: 0.25,
              explanation: 'Operational efficiency and performance',
              verificationPoints: [],
              trend: 'stable',
              lastUpdated: businessData.calculated_at || businessData.created_at
            },
            market: {
              score: parseFloat(businessData.market_score || '0'),
              weight: 0.20,
              explanation: 'Market presence and reputation',
              verificationPoints: [],
              trend: 'stable',
              lastUpdated: businessData.calculated_at || businessData.created_at
            }
          },
          redFlags: redFlags.map(flag => ({
            id: flag.id,
            type: flag.type,
            severity: flag.severity,
            title: flag.title,
            description: flag.description,
            impact: flag.impact,
            detectedAt: flag.detected_at,
            status: flag.status,
            evidence: flag.evidence || {}
          })),
          calculatedAt: businessData.calculated_at || businessData.created_at,
          calculationVersion: '1.0.0',
          dataSourcesUsed: ['MCA', 'GSTN', 'Google Business', 'IndiaMART'],
          validUntil: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        }
        
        setScore(tlbhScore)
        
        // Generate risk assessment based on actual data
        const riskScore = 100 - tlbhScore.tlbhScore
        const overallRisk = riskScore < 20 ? 'low' : riskScore < 40 ? 'medium' : riskScore < 60 ? 'high' : 'critical'
        
        // Generate risk factors based on dimension scores
        const riskFactors: any[] = []
        
        // Check each dimension for potential risks
        if (tlbhScore.dimensionScores.legal.score < 70) {
          riskFactors.push({
            category: 'Legal Compliance',
            factor: 'Legal Score Below Threshold',
            impact: tlbhScore.dimensionScores.legal.score < 50 ? 'high' : 'medium',
            likelihood: 'medium',
            description: `Legal compliance score of ${tlbhScore.dimensionScores.legal.score.toFixed(2)} indicates potential regulatory or compliance issues.`,
            mitigation: 'Review and update compliance procedures, ensure all registrations are current, and consider engaging legal counsel.'
          })
        }
        
        if (tlbhScore.dimensionScores.financial.score < 70) {
          riskFactors.push({
            category: 'Financial Health',
            factor: 'Financial Score Below Threshold',
            impact: tlbhScore.dimensionScores.financial.score < 50 ? 'high' : 'medium',
            likelihood: 'medium',
            description: `Financial health score of ${tlbhScore.dimensionScores.financial.score.toFixed(2)} suggests potential financial stability concerns.`,
            mitigation: 'Implement robust financial monitoring, improve cash flow management, and consider financial restructuring if needed.'
          })
        }
        
        if (tlbhScore.dimensionScores.operational.score < 70) {
          riskFactors.push({
            category: 'Operational Efficiency',
            factor: 'Operational Score Below Threshold',
            impact: tlbhScore.dimensionScores.operational.score < 50 ? 'high' : 'medium',
            likelihood: 'medium',
            description: `Operational efficiency score of ${tlbhScore.dimensionScores.operational.score.toFixed(2)} indicates process or efficiency challenges.`,
            mitigation: 'Streamline operations, implement process improvements, and invest in operational technology.'
          })
        }
        
        if (tlbhScore.dimensionScores.market.score < 70) {
          riskFactors.push({
            category: 'Market Presence',
            factor: 'Market Score Below Threshold',
            impact: 'medium',
            likelihood: 'medium',
            description: `Market presence score of ${tlbhScore.dimensionScores.market.score.toFixed(2)} suggests limited market visibility or reputation issues.`,
            mitigation: 'Enhance marketing efforts, improve customer engagement, and build stronger brand presence.'
          })
        }
        
        // Add risk factors based on red flags
        redFlags.forEach(flag => {
          riskFactors.push({
            category: 'Compliance Alert',
            factor: flag.title,
            impact: flag.severity === 'critical' ? 'high' : flag.severity === 'high' ? 'high' : flag.severity === 'medium' ? 'medium' : 'low',
            likelihood: 'high',
            description: flag.description,
            mitigation: 'Address the identified issue promptly to prevent further impact on business score.'
          })
        })
        
        // If no specific risks, add general assessment
        if (riskFactors.length === 0) {
          riskFactors.push({
            category: 'General Assessment',
            factor: 'Strong Overall Performance',
            impact: 'low',
            likelihood: 'low',
            description: 'Business demonstrates strong performance across all dimensions with no significant risk factors identified.',
            mitigation: 'Continue current practices and maintain regular monitoring to sustain performance levels.'
          })
        }
        
        // Generate recommendations
        const recommendations: string[] = []
        
        if (redFlags.length > 0) {
          recommendations.push(`Address ${redFlags.length} active red flag${redFlags.length > 1 ? 's' : ''} to improve overall score`)
        }
        
        if (tlbhScore.dimensionScores.legal.score < 80) {
          recommendations.push('Strengthen legal compliance and regulatory adherence')
        }
        
        if (tlbhScore.dimensionScores.financial.score < 80) {
          recommendations.push('Improve financial health through better cash flow management')
        }
        
        if (tlbhScore.dimensionScores.operational.score < 80) {
          recommendations.push('Enhance operational efficiency and process optimization')
        }
        
        if (tlbhScore.dimensionScores.market.score < 80) {
          recommendations.push('Boost market presence and customer engagement')
        }
        
        if (tlbhScore.confidence < 80) {
          recommendations.push('Improve data quality and completeness for more accurate assessments')
        }
        
        if (recommendations.length === 0) {
          recommendations.push('Maintain current performance levels and continue best practices')
          recommendations.push('Regular monitoring and periodic reviews to sustain excellence')
        }
        
        const assessment: RiskAssessment = {
          businessId: businessData.id,
          overallRisk: overallRisk as any,
          riskScore: Math.round(riskScore),
          riskFactors,
          recommendations,
          assessmentDate: new Date().toISOString(),
          validUntil: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
          assessor: 'Automated Risk Assessment System'
        }
        
        setRiskAssessment(assessment)
        
      } catch (error) {
        console.error('Failed to load risk assessment:', error)
        showToast.showToast({
          type: 'error',
          title: 'Loading Failed',
          message: 'Unable to load risk assessment data. Please try again.'
        })
      } finally {
        setIsLoading(false)
      }
    }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading risk assessment...</span>
      </div>
    )
  }

  // Business selector component
  const BusinessSelector = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        Select a Business to Assess
      </h2>
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by business name, PAN, or GSTIN..."
            value={businessSearchQuery}
            onChange={(e) => setBusinessSearchQuery(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
        </div>
        <button
          onClick={handleBusinessSearch}
          disabled={isSearching || !businessSearchQuery.trim()}
          className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSearching ? <LoadingSpinner size="sm" /> : 'Search'}
        </button>
      </div>

      {/* Search Results */}
      {businessSearchResults.length > 0 && (
        <div className="mt-4 border border-gray-200 dark:border-gray-700 rounded-lg max-h-64 overflow-y-auto">
          {businessSearchResults.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
              onClick={() => handleSelectBusiness(item)}
            >
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 dark:text-white">{item.business_name}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {item.industry_sector} • PAN: {item.pan}
                    {item.score ? ` • Score: ${parseFloat(item.score).toFixed(1)}` : ''}
                  </p>
                </div>
              </div>
              <button className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded text-sm font-medium">
                Assess
              </button>
            </div>
          ))}
        </div>
      )}

      {businessSearchResults.length === 0 && businessSearchQuery && !isSearching && (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          No businesses found. Try a different search term.
        </p>
      )}
    </div>
  )

  if (!business || !score || !riskAssessment) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Risk Assessment
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Search and select a business to view its risk assessment
          </p>
        </div>
        <BusinessSelector />
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No business selected
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Search for a business above to view its detailed risk assessment.
          </p>
        </div>
      </div>
    )
  }

  // Convert business data to Business type format
  const businessFormatted: Business = {
    id: business.id,
    name: business.business_name,
    pan: business.pan,
    gstin: business.gstin || 'Not registered',
    industry: business.industry_sector,
    businessType: business.business_type as any,
    registrationDate: business.registration_date,
    contactEmail: business.contact_email || 'Not provided',
    contactPhone: business.contact_phone || 'Not provided',
    address: {
      street: business.registered_address || '',
      city: business.city || '',
      state: business.state || '',
      pincode: business.pincode || '',
      country: 'India'
    },
    ownerId: business.owner_email || '',
    createdAt: business.created_at,
    updatedAt: business.updated_at || business.created_at
  }

  return (
    <div className="space-y-6">
      {/* Business Selector Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h3 className="font-medium text-gray-900 dark:text-white">{business.business_name}</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">{business.industry_sector} • PAN: {business.pan}</p>
            </div>
          </div>
          <button
            onClick={() => {
              setBusiness(null)
              setScore(null)
              setRiskAssessment(null)
              navigate('/lender/risk-assessment')
            }}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            Change Business
          </button>
        </div>
      </div>

      <RiskAssessmentDashboard
        business={businessFormatted}
        score={score}
        riskAssessment={riskAssessment}
      />
    </div>
  )
}